import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';

import { getPopupRegistration, openPopup, Priority } from 'app/PopupHolder';
import SimpleModal from 'components/base/SimpleModal';
import SVG from 'components/base/SVG';
import nationalDncIcon from 'assets/images/national-dnc.svg';

import { UsStateShortOptions } from 'data/user/constants';
import { loadContact, saveContact, selectLoading } from 'data/contacts';
import { materializeInvestorContacts } from 'data/investors';
import { toInvestorContactRequest } from 'data/investors/investorUtils';
import { cleanPhone, isValidEmail } from 'utils/validation';

import Dropdown from '../SearchFilter/Dropdown';
import css from './style.scss';

// "Contact Information" edit card — Figma node 383:25097 ("type = investor,
// status = Default"), fetched directly rather than guessed from a
// screenshot after getting it wrong twice already (an inline icon, then a
// round-icon-button layout that didn't match this node at all). Matches that
// node's exact structure: 44px form controls throughout, text "+ Add" links
// (not round icon buttons) per section, a circular trash button per row, and
// the DNC control rendered as a dropdown ("No DNC" / "My DNC") rather than a
// toggle switch — Figma redesigned it that way from ContactEditor's original
// toggle, so this follows Figma over that precedent. "Public DNC" stays a
// read-only pill (not user-editable either way).
//
// Wired to the classic contact save endpoint (ATAI-115): the investor's
// materialized Contact record (id = investor.id) is the same entity
// ContactEditor/ContactProfile already edit, so this reuses their
// `loadContact`/`saveContact` actions rather than inventing a new endpoint.
// The modal opens from the vendor-shaped `investor.contacts`/
// `mailingAddresses` for an instant first paint, then replaces that with the
// real persisted Contact (phones/emails/mailing address) once `loadContact`
// resolves -- Save always PUTs the full loaded contact back (spread first,
// edited fields last) so untouched fields like typeId/statusId round-trip
// unchanged, the same pattern ContactEditor/ContactProfile's own toDraft use.
const CONTROL_HEIGHT = 44;

const PHONE_TYPES = [
  { label: 'Mobile', value: 'MOBILE' },
  { label: 'Landline', value: 'LANDLINE' },
  { label: 'Other', value: 'OTHER' },
];

const EMAIL_TYPES = [
  { label: 'Home', value: 'HOME' },
  { label: 'Work', value: 'WORK' },
  { label: 'Other', value: 'OTHER' },
];

const DNC_OPTIONS = [
  { label: 'No DNC', value: 'NO_DNC' },
  { label: 'My DNC', value: 'MY_DNC' },
];

// Real investor contacts arrive as { name, phones: [{ number, dnc }], emails:
// [string] } (the interim endpoint's actual shape — see
// ../../../data/investors/investorRequestResponseMiddleware) — reshaped here
// into the { type, phone/email, seq, publicDNC, dncPab } items this editor
// works with.
const toPhoneItems = (contacts) =>
  (contacts || [])
    .flatMap((contact) => contact.phones || [])
    .map((phone, seq) => ({
      type: PHONE_TYPES[0].value,
      phone: phone.number || '',
      seq,
      publicDNC: Boolean(phone.dnc),
      dncPab: null,
    }));

const toEmailItems = (contacts) =>
  (contacts || [])
    .flatMap((contact) => contact.emails || [])
    .map((email, seq) => ({
      type: EMAIL_TYPES[0].value,
      email: (email && email.email) || email || '',
      seq,
    }));

// ATAI-364: US numbers only -- matches production's 10-digit limit (the shared
// isValidPhone helper accepts up to 15 digits, which is too loose here).
const PHONE_DIGITS = 10;

const normalizePhone = (phone = '') => (phone || '').replace(/\D/g, '');

// ATAI-364: a blank row (freshly added via "+Add", not yet filled in) isn't
// "invalid" -- the save endpoint already treats a no-value item as nothing
// to persist (ObjUtil.noValue, UserService#saveContactItems), so blocking
// Save over an untouched blank row would just be friction with nothing to
// show for it. Only a row with *some* value that doesn't parse blocks Save.
const isPhoneInvalid = (value) => {
  const trimmed = (value || '').trim();

  // cleanPhone drops a leading country code, so an already-saved 11-digit
  // "1XXXXXXXXXX" still counts as a valid 10-digit number.
  return trimmed !== '' && (cleanPhone(trimmed) || '').length !== PHONE_DIGITS;
};

const isEmailInvalid = (value) => {
  const trimmed = (value || '').trim();

  return trimmed !== '' && !isValidEmail(trimmed);
};

// ATAI-449: two rows holding the same email or phone number are duplicates --
// emails compare case-insensitively, phones by digits (cleanPhone drops a
// leading country code, so "1XXXXXXXXXX" matches "XXXXXXXXXX"). The oldest row
// (lowest seq -- "+Add" prepends new rows with a higher seq, so display order
// is newest first) keeps the value; the newer repeat is the duplicate, so the
// user removes what they just added rather than being told the saved one is
// wrong. Blank rows have no key and never collide.
const phoneKey = (value) => cleanPhone(value) || normalizePhone(value);
const emailKey = (value) => (value || '').trim().toLowerCase();

// ATAI-373: rows painted from the search payload carry no persisted `id`.
// When Save had to materialize the Contact first, that record may already
// hold the same numbers/emails (e.g. a skip-traced investor never saved to a
// list), so a row matching one by value takes its id (and dncPab) rather than
// going up as a second copy. Rows that already have an id are left alone.
const adoptIds = (items, persisted, field, keyOf) => {
  const byKey = {};
  (persisted || []).forEach((p) => {
    const key = keyOf(p[field]);
    if (key && p.id != null && !byKey[key]) byKey[key] = p;
  });

  return items.map((item) => {
    if (item.id != null) return item;
    const match = byKey[keyOf(item[field])];
    if (!match) return item;
    return field === 'phone'
      ? { ...item, id: match.id, dncPab: item.dncPab ?? match.dncPab ?? null }
      : { ...item, id: match.id };
  });
};

const findDuplicateSeqs = (items, field, keyOf) => {
  const seen = new Set();
  const duplicates = new Set();

  const oldestFirst = [...items].sort((a, b) => a.seq - b.seq);

  oldestFirst.forEach((item) => {
    const key = keyOf(item[field]);
    if (!key) return;

    if (seen.has(key)) duplicates.add(item.seq);
    else seen.add(key);
  });

  return duplicates;
};

// The persisted Contact's own phones/emails ({ id, phone/email, type,
// dncPab }, see propstream-be's Contact/ContactPhone/ContactEmail) --
// `publicDNC` isn't a field on ContactPhone itself (it's @JsonIgnore there);
// it's derived the same way ContactProfile/contactState.js's
// mergePhonesWithAppendedPhones does, by matching against the contact's
// separate `appendedPhones` list.
const toPhoneItemsFromContact = (phones = [], appendedPhones = []) => {
  const appendedByPhone = {};
  (appendedPhones || []).forEach((ap) => {
    const key = normalizePhone(ap.phone);
    if (key && !appendedByPhone[key]) appendedByPhone[key] = ap;
  });

  return (phones || []).map((p, seq) => ({
    id: p.id,
    type: p.type || PHONE_TYPES[0].value,
    phone: p.phone || '',
    seq,
    dncPab: p.dncPab || null,
    publicDNC: Boolean(appendedByPhone[normalizePhone(p.phone)]),
  }));
};

const toEmailItemsFromContact = (emails = []) =>
  (emails || []).map((e, seq) => ({
    id: e.id,
    type: e.type || EMAIL_TYPES[0].value,
    email: e.email || '',
    seq,
  }));

// Same hover popup Contacts/ContactEditor's own "Contact Priority" info icon
// uses — a teal card with a title/close row and body text, opened on hover
// and kept open while the pointer is over either the icon or the popup
// itself (the short close delay is what lets the pointer travel between
// them without the popup vanishing first). Rebuilt with its own styles
// rather than reusing that component's classes for the same reason as
// ContactFieldList: they're SCSS-nested under ContactEditor's own page
// wrapper and don't apply outside it.
const InfoTooltip = ({ title, children }) => {
  const [open, setOpen] = useState(false);
  const closeTimer = useRef(null);

  const cancelClose = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };

  const scheduleClose = () => {
    cancelClose();
    closeTimer.current = setTimeout(() => setOpen(false), 150);
  };

  useEffect(() => cancelClose, []);

  return (
    <div className={css.infoTooltip}>
      <button
        type="button"
        className={css.infoTooltip__trigger}
        onMouseEnter={() => {
          cancelClose();
          setOpen(true);
        }}
        onMouseLeave={scheduleClose}
        onClick={() => setOpen((o) => !o)}
        aria-label="More information"
      >
        <SVG icon="cp-info" className={css.infoTooltip__icon} />
      </button>
      <div
        className={classNames(css.infoTooltip__content, {
          [css.infoTooltip__contentOpen]: open,
        })}
        onMouseEnter={cancelClose}
        onMouseLeave={scheduleClose}
      >
        <div className={css.infoTooltip__header}>
          <span>{title}</span>
          <button
            type="button"
            className={css.infoTooltip__close}
            onClick={() => setOpen(false)}
            aria-label="Close"
          >
            &times;
          </button>
        </div>
        <p className={css.infoTooltip__body}>{children}</p>
      </div>
    </div>
  );
};

// `cp-plus` (Figma's "plusCircle" badge, node 383:24993 / 383:25014) — a
// fixed teal-circle-with-white-plus icon, not a plain glyph. It has no
// hover-swap counterpart in Figma, so unlike editButton/deleteButton's
// icons it intentionally stays the same on hover/active rather than
// recoloring with the pill.
const SectionHeader = ({ title, count, showInfo, onAdd }) => (
  <div className={css.sectionHeader}>
    <div className={css.sectionHeader__titleGroup}>
      <span className={css.sectionHeader__title}>
        {title} ({count})
      </span>
      {!showInfo ? null : (
        <InfoTooltip title="Contact Priority">
          The Contact Priority provides the recommended calling order. Call the top-ranked number
          first to begin your outreach.
        </InfoTooltip>
      )}
    </div>
    <button type="button" className={css.addButton} onClick={onAdd}>
      <SVG icon="cp-plus" className={css.addButton__icon} />
      Add
    </button>
  </div>
);

const DncControl = ({ item, onChange }) => {
  if (item.publicDNC) {
    return (
      <div className={css.publicDncPill}>
        <img src={nationalDncIcon} alt="Public DNC" className={css.publicDncPill__icon} />
        <span>Public DNC</span>
      </div>
    );
  }

  const value = item.dncPab && item.dncPab.includes('D') ? 'MY_DNC' : 'NO_DNC';

  return (
    <Dropdown
      value={value}
      options={DNC_OPTIONS}
      onChange={(next) => onChange({ ...item, dncPab: next === 'MY_DNC' ? 'D' : null })}
      width={148}
      height={CONTROL_HEIGHT}
    />
  );
};

const ContactFieldRow = ({
  item,
  field,
  types,
  valueWidth,
  invalid,
  errorMessage,
  onChange,
  onRemove,
}) => (
  <div className={css.contactFieldRow}>
    <Dropdown
      value={item.type}
      options={types}
      onChange={(value) => onChange({ ...item, type: value })}
      width={128}
      height={CONTROL_HEIGHT}
    />
    <div className={css.contactFieldRow__valueGroup} style={{ width: valueWidth }}>
      <input
        className={classNames(css.contactFieldRow__input, {
          [css['contactFieldRow__input--error']]: invalid,
        })}
        value={item[field]}
        onChange={(ev) => {
          // ATAI-364: phone is restricted to digits as you type (no letters
          // to reject in the first place), so unlike email it needs no
          // error message explaining a bad character -- the red border
          // (still driven by isPhoneInvalid, e.g. too few digits) is enough.
          // Also capped at 10 digits, so too many can't be entered either.
          const next =
            field === 'phone'
              ? ev.target.value.replace(/\D/g, '').slice(0, PHONE_DIGITS)
              : ev.target.value;

          onChange({ ...item, [field]: next });
        }}
      />
      {!invalid || !errorMessage ? null : (
        <span className={css.contactFieldRow__error}>{errorMessage}</span>
      )}
    </div>
    {field !== 'phone' ? null : <DncControl item={item} onChange={onChange} />}
    {/* `deleteicon`, not the sprite's `cp-trash` — cp-trash hardcodes its
        fill, so it can't recolor for the hover/click state. `deleteicon` is
        the currentColor trash icon Property Tags' own delete button uses
        (MangePropertyTagModal's .deleteIcon), which .deleteButton__icon's
        CSS color override relies on the same way. */}
    <button type="button" className={css.deleteButton} onClick={onRemove} aria-label="Remove">
      <SVG icon="deleteicon" className={css.deleteButton__icon} />
    </button>
  </div>
);

const ContactFieldList = ({
  title,
  field,
  valueWidth,
  showInfo,
  items,
  types,
  isInvalid,
  errorMessage,
  keyOf,
  duplicateMessage,
  invalidSeqs,
  onChange,
}) => {
  // Live, unlike invalidSeqs: removing or editing the repeat clears its flag.
  const duplicateSeqs = findDuplicateSeqs(items, field, keyOf);

  const handleAdd = () => {
    const seq = items.reduce((max, item) => Math.max(max, item.seq), -1) + 1;

    onChange([{ type: types[0].value, [field]: '', seq }, ...items]);
  };

  const handleItemChange = (index, next) => {
    onChange(items.map((item, i) => (i === index ? next : item)));
  };

  const handleRemove = (index) => {
    onChange(items.filter((_, i) => i !== index));
  };

  return (
    <div className={css.contactFieldListSection}>
      <SectionHeader title={title} count={items.length} showInfo={showInfo} onAdd={handleAdd} />
      <div className={css.contactFieldList}>
        {items.map((item, index) => (
          <ContactFieldRow
            key={item.seq}
            item={item}
            field={field}
            types={types}
            valueWidth={valueWidth}
            // ATAI-364: `invalidSeqs` is a snapshot taken at the last failed
            // Save click (see handleSave), not a live check -- a field only
            // turns red because Save was actually clicked while it was bad,
            // not reactively as soon as any OTHER field fails and this one
            // happens to still have old (or newly-typed) invalid text in it.
            // Still re-checks isInvalid() so fixing *this* row clears its
            // own error immediately, without waiting for another Save click.
            invalid={
              invalidSeqs.has(item.seq) && (isInvalid(item[field]) || duplicateSeqs.has(item.seq))
            }
            errorMessage={isInvalid(item[field]) ? errorMessage : duplicateMessage}
            onChange={(next) => handleItemChange(index, next)}
            onRemove={() => handleRemove(index)}
          />
        ))}
      </div>
    </div>
  );
};

const AddressField = ({ label, name, value, onChange, width }) => (
  <div className={css.editContact__field}>
    <span className={css.workflow__label}>{label}</span>
    <input
      className={css.editContact__input}
      style={{ width }}
      name={name}
      value={value}
      onChange={onChange}
    />
  </div>
);

const EditContactModal = ({ investor, contactId, closePopup, onSaved }) => {
  const dispatch = useDispatch();
  const loading = useSelector(selectLoading);
  const [rawContact, setRawContact] = useState(null);
  // ATAI-373: true while Save materializes + loads a Contact for an investor
  // that didn't have one yet (see handleSave); the save itself is covered by
  // the contacts slice's own `loading`.
  const [saving, setSaving] = useState(false);
  // ATAI-467: the rows used to paint from the vendor-shaped search contacts
  // first and then jump (count, order, types, DNC controls) when the real
  // persisted Contact loaded. With a contactId the real record is coming, so
  // hold the rows back until it lands; without one (an investor that was
  // never saved) there is nothing to wait for. A failed load falls back to
  // the search data rather than leaving the modal empty.
  const [contactReady, setContactReady] = useState(!contactId);

  const [phones, setPhones] = useState(() => toPhoneItems(investor.contacts));
  const [emails, setEmails] = useState(() => toEmailItems(investor.contacts));
  // ATAI-364: which rows (by seq) get flagged red -- set only in handleSave,
  // not reactively on every keystroke (e.g. while a 10-digit phone number is
  // still 4 digits in, or a field nobody's touched yet since the last failed
  // Save). See ContactFieldList's own comment on `invalidSeqs` for how this
  // is actually applied per field.
  const [invalidPhoneSeqs, setInvalidPhoneSeqs] = useState(() => new Set());
  const [invalidEmailSeqs, setInvalidEmailSeqs] = useState(() => new Set());
  const [address, setAddress] = useState(() => {
    const first = (investor.mailingAddresses || [])[0] || {};

    return {
      street: first.street || '',
      city: first.city || '',
      state: first.state || '',
      zip: first.zip || '',
    };
  });

  // Investor contacts arrive skip-trace-shaped from the search/profile
  // response above; replace that with the real persisted Contact (its own
  // id, typeId/statusId, and dncPab) as soon as it loads, since Save always
  // PUTs this record back. `investor.id` is the BatchData investor id (a
  // vendor hex string) -- the classic contact endpoints key on the numeric
  // materialized Contact id instead, which is `contactId` here (resolved by
  // Profile/index.js the same way its own notes/status/touchpoint calls do).
  useEffect(() => {
    if (!contactId) return undefined;

    let cancelled = false;

    const handleLoaded = ({ response, error } = {}) => {
      if (cancelled) return;

      if (error) {
        setContactReady(true);
        return;
      }

      const contact = response?.response || response || {};

      setRawContact(contact);
      setPhones(toPhoneItemsFromContact(contact.phones, contact.appendedPhones));
      setEmails(toEmailItemsFromContact(contact.emails));
      setAddress({
        street: contact.mailStreetAddress || '',
        city: contact.mailCity || '',
        state: contact.mailState || '',
        zip: contact.mailZip || '',
      });
      setContactReady(true);
    };

    // loadContact only reports failures through this callback; without it a
    // failed load left the rows hidden for good.
    const handleLoadFailed = () => {
      if (!cancelled) setContactReady(true);
    };

    dispatch(loadContact(contactId, handleLoaded, handleLoadFailed));

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactId]);

  const handleAddressChange = (ev) => {
    const { name, value } = ev.target;
    setAddress((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = () => {
    // Still loading the known Contact (rows held back), or already resolving one.
    if (saving || !contactReady) return;

    // ATAI-364: phone/email inputs took any value with no validation at all
    // -- block the actual save, rather than just decorating the fields,
    // since nothing upstream (this form, the save action, or the backend's
    // own saveContactItems) was rejecting a malformed value otherwise.
    const badPhones = phones.filter((p) => isPhoneInvalid(p.phone));
    const badEmails = emails.filter((e) => isEmailInvalid(e.email));

    // ATAI-449: the same email/phone twice also blocks Save.
    const duplicatePhoneSeqs = findDuplicateSeqs(phones, 'phone', phoneKey);
    const duplicateEmailSeqs = findDuplicateSeqs(emails, 'email', emailKey);

    if (
      badPhones.length ||
      badEmails.length ||
      duplicatePhoneSeqs.size ||
      duplicateEmailSeqs.size
    ) {
      setInvalidPhoneSeqs(new Set([...badPhones.map((p) => p.seq), ...duplicatePhoneSeqs]));
      setInvalidEmailSeqs(new Set([...badEmails.map((e) => e.seq), ...duplicateEmailSeqs]));
      return;
    }

    const persist = (base) => {
      const contact = {
        ...base,
        phones: adoptIds(phones, base.phones, 'phone', phoneKey).map(
          ({ publicDNC: _publicDNC, ...phone }) => phone
        ),
        emails: adoptIds(emails, base.emails, 'email', emailKey),
        mailStreetAddress: address.street,
        mailCity: address.city,
        mailState: address.state,
        mailZip: address.zip,
        // The API echoes display-only `type`/`status` labels alongside the
        // ids; drop them so only typeId/statusId round-trip, same as
        // ContactEditor/ContactProfile's own toDraft.
        type: undefined,
        status: undefined,
      };

      dispatch(
        saveContact(contact, () => {
          onSaved?.();
          closePopup?.();
        })
      );
    };

    if (rawContact) {
      persist(rawContact);
      return;
    }

    // ATAI-373: an investor that was never skip traced or saved to a list has
    // no materialized Contact yet (no `contactId`, so nothing was loaded), and
    // Save used to return here silently -- no request at all. Materialize it
    // first (dedupes on the BatchData investor id, so an existing Contact is
    // returned rather than duplicated -- same lookup useDialInvestorPhone
    // uses), load that record, then save the edits onto it as usual.
    const fail = (message) => {
      setSaving(false);
      console.warn(message, investor.id);
    };

    setSaving(true);
    dispatch(
      materializeInvestorContacts(
        { investors: [toInvestorContactRequest(investor)] },
        ({ response }) => {
          const resolvedId = response?.results?.[0]?.contactId;
          if (resolvedId == null) {
            fail('Could not resolve a contact to save for investor');
            return;
          }

          dispatch(
            loadContact(
              resolvedId,
              ({ response: loaded, error } = {}) => {
                if (error) {
                  fail('Could not load the contact to save for investor');
                  return;
                }

                const base = loaded?.response || loaded || {};
                setSaving(false);
                setRawContact(base);
                persist(base);
              },
              () => fail('Could not load the contact to save for investor')
            )
          );
        },
        () => fail('Could not resolve a contact to save for investor')
      )
    );
  };

  return (
    <SimpleModal
      isOpen
      uniqueId="edit-contact-info-modal"
      onCancel={closePopup}
      onSubmit={handleSave}
      loading={loading || saving}
      modalClassName={css.editContactPanel}
      modalFooterWrapperClassName={css.editContactFooter}
      title="Contact Information"
      submitText="Save"
      cancelText="Cancel"
      modalCancelBtnClassName={css.editContactCancelBtn}
      modalSubmitBtnClassName={css.editContactSaveBtn}
      modalBody={
        <div className={classNames(css.editContact, { [css.editContactPending]: !contactReady })}>
          <ContactFieldList
            title="Phone Numbers"
            field="phone"
            valueWidth={275}
            showInfo
            items={phones}
            types={PHONE_TYPES}
            isInvalid={isPhoneInvalid}
            keyOf={phoneKey}
            duplicateMessage="This phone number is already added."
            invalidSeqs={invalidPhoneSeqs}
            onChange={setPhones}
          />

          <div className={css.divider} />

          <ContactFieldList
            title="Email Addresses"
            field="email"
            valueWidth={438}
            items={emails}
            types={EMAIL_TYPES}
            isInvalid={isEmailInvalid}
            errorMessage="Enter a valid email address."
            keyOf={emailKey}
            duplicateMessage="This email address is already added."
            invalidSeqs={invalidEmailSeqs}
            onChange={setEmails}
          />

          <div className={css.divider} />

          <div className={css.addressSection}>
            <p className={css.addressSection__title}>Mailing Address</p>
            <div className={css.editContact__row}>
              <AddressField
                label="Street"
                name="street"
                value={address.street}
                onChange={handleAddressChange}
                width={444}
              />
              <AddressField
                label="City"
                name="city"
                value={address.city}
                onChange={handleAddressChange}
                width={188}
              />
            </div>
            <div className={css.editContact__row}>
              <div className={css.editContact__field}>
                <span className={css.workflow__label}>State</span>
                <Dropdown
                  value={address.state}
                  options={UsStateShortOptions}
                  onChange={(value) => setAddress((prev) => ({ ...prev, state: value }))}
                  width={321}
                  height={CONTROL_HEIGHT}
                />
              </div>
              <AddressField
                label="Zip"
                name="zip"
                value={address.zip}
                onChange={handleAddressChange}
                width={310}
              />
            </div>
          </div>
        </div>
      }
    />
  );
};

const registrationId = getPopupRegistration(EditContactModal);
// HIGHEST, not MEDIUM: this app's popup priorities are separate stacked
// layers, not one z-index — same-priority popups queue FIFO behind
// whichever is already showing (PopupHolder/reducer.js's `queue.push` /
// `getIn([0])`), so opening this at the Investor Profile modal's own
// priority (MEDIUM) left it queued invisibly behind it instead of stacking
// on top. HIGHEST matches how Confirm/DNCPolicyModal already open from
// within another already-open modal.
EditContactModal.open = (props = {}) =>
  openPopup(registrationId, { ...props, priority: Priority.HIGHEST });

export default EditContactModal;
